import React from "react";

/**
 * Loading placeholders that hold the exact shape of what is coming.
 *
 * A spinner says "wait"; a skeleton says "this is where your products will be" — the grid
 * keeps its layout, so nothing jumps when the real tiles land. The shimmer is one band of
 * light sweeping across (the `shimmer` keyframe in tailwind.config.js) and stops under
 * prefers-reduced-motion, where the placeholder simply sits still.
 *
 *   <SkeletonBlock className="h-4 w-24" />   any rectangle, sized by its classes
 *   <SkeletonTile />                         one POS tile (ProdWidget / ServiceWidget)
 *   <SkeletonChip />                         one category chip (FilterChip compact)
 */
export const SkeletonBlock = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`relative overflow-hidden bg-slate-200/60 ${className}`}
  >
    <div
      className="absolute inset-0 -translate-x-full animate-shimmer motion-reduce:hidden
        bg-gradient-to-r from-transparent via-white/70 to-transparent"
    />
  </div>
);

// The same 160px card a POS tile is (pages/Sales/POS/Products/CatalogTile), laid out the
// way the tile will be: the picture panel on top, the name's two lines and the price
// below it. Matching the real tile is the point — when the products land, nothing moves.
export const SkeletonTile = () => (
  <div
    aria-hidden="true"
    className="relative h-[160px] overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200/70
      shadow-[0_1px_3px_rgba(15,23,42,0.06),0_4px_12px_rgba(15,23,42,0.06)]"
  >
    <SkeletonBlock className="absolute inset-x-0 top-0 h-[92px] !bg-slate-100" />
    <div className="absolute inset-x-0 bottom-0 h-[68px] px-3 pt-2.5 flex flex-col gap-[6px] border-t border-slate-100">
      <SkeletonBlock className="h-[10px] w-[85%] rounded-full" />
      <SkeletonBlock className="h-[10px] w-[55%] rounded-full" />
      <SkeletonBlock className="mt-auto mb-2.5 h-[13px] w-[38%] rounded-full" />
    </div>
  </div>
);

// A compact FilterChip's footprint (26px), at one of a few widths so a row of them does
// not read as a ruler.
const CHIP_WIDTHS = ["w-[64px]", "w-[104px]", "w-[84px]", "w-[120px]", "w-[92px]"];
export const SkeletonChip = ({ index = 0 }) => (
  <SkeletonBlock
    className={`h-[26px] shrink-0 rounded-full ${CHIP_WIDTHS[index % CHIP_WIDTHS.length]}`}
  />
);
